<template>
    <div
        class="mainPage onboarding"
        v-bind:class="`JARVIS_View_Main_${thisPage}`"
    >
        <SportsBanner v-if="!isFromApp" v-bind:bannerPosition="BannerPosition.CastorTop" />
        <Component
            v-bind:is="sportSwitchComponent"
            v-bind:style="sportSwitchStyle"
            class="headerSticky"
        />

        <MultipleSportsTabs
            v-if="isInMultipleSportsZone || isInSimulationSportsZone"
            class="headerSticky"
            v-bind:style="marketSwitchStyle"
            v-bind:routePage="routePage"
        />
        <MarketSwitch
            v-else
            class="headerSticky"
            v-bind:style="marketSwitchStyle"
        />

        <Collapse
            v-if="haveEvent"
            class="headerSticky"
            v-bind:style="collapseStyle"
            v-bind:status="isExpandAll"
            v-bind:isMoreMarket="false"
            collapseKey="sportsPage_collapseList"
            expandKey="sportsPage_expandList"
            v-on:changeStatus="toggleIsExpandAll"
            v-on:toggleFilter="toggleFilter"
        />

        <FeaturedSection v-if="loaded" v-bind:haveEvent="haveEvent" />

        <div class="oddContent">
            <Spinner v-if="!loaded" />
            <NoContent
                v-else-if="!haveEvent"
                imgType="noRecords"
                v-bind:title="i18n.t('myMatches_noMatches')"
                v-bind:content="i18n.t('sportsEventsNoMatches')"
            >
                <SwitchToOtherMarket
                    v-if="shouldShowNoMatchesMarketSwitchButton"
                    v-bind:switchTo="switchTo"
                    v-bind:hasMatch="haveEvent"
                />
            </NoContent>

            <div v-else>
                <Component
                    v-bind:is="leagueComponent"
                    v-for="matches in filteredLeagueEvents"
                    v-bind:key="`${matches[0].type === 'match' ? matches[0].leagueId : matches[0].id}-${marketPage}-${matches[0].type === 'match' ? matches[0].eventId : matches[0].leagueCode}-${matches[0].isLive}`"
                    v-bind:matches="matches"
                />
            </div>
            <SwitchToOtherMarket
                v-if="!!haveEvent && !isInMultipleSportsZone"
                v-bind:switchTo="switchTo"
                v-bind:hasMatch="haveEvent"
            />
        </div>

        <LeagueFilter
            v-if="showLeagueFilter"
            v-bind:key="`filter_${sportType}_${marketPage}_${isMixParlayMode}`"
            v-bind:leagueEvents="leagueEvents"
            v-on:onClose="toggleFilter"
        />

        <template v-if="isNewWidgetSwitchEnabled">
            <WidgetSwitch2 v-if="isAsi && !isFromApp" />
        </template>
        <template v-else>
            <Transition name="fade">
                <WidgetSwitch v-if="isAsi && !isFromApp && !isMixParlayMode && !isShowMediumBetBuilderBetSlip" />
            </Transition>
        </template>
    </div>
</template>

<script lang="ts">
    import cloneDeep from 'lodash/cloneDeep';
    import orderBy from 'lodash/orderBy';
    import throttle from 'lodash/throttle';
    import { storeToRefs } from 'pinia';
    import { computed, defineComponent, ref, watch } from 'vue';
    import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';
    import Collapse from '@/components/Collapse.vue';
    import League from '@/components/League.vue';
    import LeagueFilter from '@/components/LeagueFilter.vue';
    import MarketSwitch from '@/components/MarketSwitch.vue';
    import MultipleSportsTabs from '@/components/MultipleSportsTabs.vue';
    import SportsBanner from '@/components/SportsBanner.vue';
    import SportsZoneFlagsBanner from '@/components/SportsZoneFlagsBanner.vue';
    import SwitchToOtherMarket from '@/components/SwitchToOtherMarket.vue';
    import LazyLoad from '@/components/common/LazyLoad.vue';
    import NoContent from '@/components/common/NoContent.vue';
    import Spinner from '@/components/common/Spinner.vue';
    import FeaturedSection from '@/components/sportLobby/FeaturedSection.vue';
    import SportsSwitch from '@/components/sportsSwitch/SportsSwitch.vue';
    import useZoneBanner from '@/components/useZoneBanner';
    import WidgetSwitch from '@/components/widget/WidgetSwitch.vue';
    import WidgetSwitch2 from '@/components/widget/WidgetSwitch2.vue';
    import EuroSportsSwitch from '@/indiaSite/components/EuroSportsSwitch.vue';
    import IndiaLeague from '@/indiaSite/components/IndiaLeague.vue';
    import type { IMatch } from '@/interface/IMatch';
    import type { IOutrightTournament } from '@/interface/IOutright';
    import { BannerPosition, MarketPage, RoutePage, SportType } from '@/interface/enum';
    import { useApp } from '@/libraries/composables/useApp';
    import useScroll from '@/libraries/composables/useScroll';
    import { useZone } from '@/libraries/composables/useZone';
    import BusinessLogicHelper from '@/libraries/helpers/businessLogicHelper';
    import { getSportTypeKey } from '@/libraries/helpers/sportsHelper';
    import { i18n } from '@/libraries/i18n';
    import { useEventsQuery } from '@/libraries/oddsApi/composables/useEventsQuery';
    import { useOutrightTournaments } from '@/libraries/oddsApi/composables/useOutrightTournaments';
    import { groupByWithOrder } from '@/libraries/utils/utils';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import { useOddsApiStore } from '@/stores/oddsApiStore';
    import { useRootStore } from '@/stores/rootStore';

    const ROUTE_PAGE = RoutePage.MultipleSportsZone;

    export default defineComponent({
        name: 'MainView',
        components: {
            FeaturedSection,
            SportsBanner,
            SwitchToOtherMarket,
            MarketSwitch,
            MultipleSportsTabs,
            Collapse,
            League,
            Spinner,
            LeagueFilter,
            NoContent,
            IndiaLeague,
            WidgetSwitch,
            WidgetSwitch2,
            SportsZoneFlagsBanner,
            LazyLoad,
        },
        setup() {
            const { sportType, marketPage, isAsi, isExpandAll, isMixParlayMode, marketPageEventCount, isWsConnected, filter } = storeToRefs(useRootStore());

            const { sportSwitchStyle, marketSwitchStyle, collapseStyle } = useScroll();
            const { customerSettings } = storeToRefs(useOddsApiStore());

            const {
                isInEFootballZone,
                isInMultipleSportsZone,
                isInSimulationSportsZone,
                isInAnyZone,
                leagueIds,
                leagueNames,
                shouldShowNoMatchesMarketSwitchButton,
            } = useZone();

            const routePage = computed(() => (isInSimulationSportsZone.value ? RoutePage.SimulationSportsZone : ROUTE_PAGE));
            const { isShowFlagsBanner } = useZoneBanner();

            const { isEuroSite, isShowLiveInToday, isNewWidgetSwitchEnabled } = storeToRefs(useFeatureToggleStore());

            const sportSwitchComponent = computed(() => {
                if (isEuroSite.value) return EuroSportsSwitch;
                return SportsSwitch;
            });

            const { isShowMediumBetBuilderBetSlip } = storeToRefs(useBetSlipStore());

            const thisPage = computed(() => getSportTypeKey(sportType.value));

            const isOutright = computed(() => marketPage.value === MarketPage.Outright);

            const { outrightTournaments, refetch: refetchOutrightTournaments, loaded: outrightTournamentsLoaded } = useOutrightTournaments({
                isEnabled: isOutright,
                sportType,
                leagueNames,
                leagueIds,
            });
            const isEventsQueryEnabled = computed(() => !isOutright.value);

            const isIncludeLiveInToday = computed(() => isEuroSite.value && isShowLiveInToday.value);

            const { events, refetch: refetchEvents, loaded: eventsLoaded } = useEventsQuery({
                sportType,
                marketPage,
                isMixParlayMode,
                isIncludeLiveInToday,
                tournamentIds: leagueIds,
                tournamentNames: leagueNames,
                isEnabled: isEventsQueryEnabled,
            });

            const marketPages = [MarketPage.Live, MarketPage.Today, MarketPage.EarlyMarket, MarketPage.Outright];
            const switchTo = computed(() => {
                const pages = marketPages.filter(x => x !== marketPage.value);
                const switchToPage = pages.find(key => marketPageEventCount.value[key] && marketPageEventCount.value[key] > 0);
                if (switchToPage) {
                    return switchToPage;
                }
                if (marketPage.value === MarketPage.Live) {
                    return MarketPage.Today;
                }
                return MarketPage.Live;
            });

            function initMatchOrder(eventGroupedByLeague: (IMatch | IOutrightTournament)[][]) {
                let matchOrder = 0;
                eventGroupedByLeague.forEach((eventsGrouped) => {
                    eventsGrouped.forEach((event) => {
                        matchOrder++;
                        if ('matchOrder' in event) {
                            event.matchOrder = matchOrder;
                        }
                    });
                });
            }

            function formatEvents(events: (IMatch | IOutrightTournament)[] = []) {
                const clonedEvents = cloneDeep(events);
                const liveEvents = clonedEvents.filter(e => e.isLive);
                const liveSorted = orderBy(liveEvents, x => (x.type === 'match' ? x.eventCode : x.leagueCode));
                const liveGrouped = groupByWithOrder(liveSorted, x => (x.type === 'match' ? x.leagueId.toString() : x.id.toString()));

                const nonLiveEvents = clonedEvents.filter(e => !e.isLive);
                const nonLiveSorted = orderBy(nonLiveEvents, x => (x.type === 'match' ? x.eventCode : x.leagueCode));
                const nonLiveGrouped = groupByWithOrder(nonLiveSorted, x => (x.type === 'match' ? x.leagueId.toString() : x.id.toString()));
                const eventGroupedByLeague = liveGrouped.concat(nonLiveGrouped);

                initMatchOrder(eventGroupedByLeague);

                return eventGroupedByLeague;
            }

            const eventsDisplay = computed<(IMatch | IOutrightTournament)[]>(() => {
                if (!BusinessLogicHelper.isAllowedSport(customerSettings.value, SportType.toOddsApi(sportType.value))) {
                    return [];
                }
                return marketPage.value === MarketPage.Outright
                    ? outrightTournaments.value
                    : events.value;
            });

            const loaded = computed(() => (marketPage.value === MarketPage.Outright
                ? outrightTournamentsLoaded.value
                : eventsLoaded.value));

            const leagueEvents = computed(() => formatEvents(eventsDisplay.value));

            const haveEvent = computed(() => {
                const haveSportsEvent = eventsDisplay.value && eventsDisplay.value.length > 0;
                return haveSportsEvent;
            });

            const leagueComponent = computed(() => (isEuroSite.value ? IndiaLeague : League));

            const filteredLeagueEvents = computed(() => {
                if (!eventsDisplay.value) return [];
                if (!filter.value) return leagueEvents.value;

                let leagues: (IMatch | IOutrightTournament)[] = [...eventsDisplay.value];
                if (filter.value?.leagueIds?.length !== 0) {
                    leagues = leagues.filter(x => filter.value?.leagueIds.includes(x.type === 'match' ? x.leagueId : x.id));
                }
                const startDateTime = filter.value?.startDateTime;
                if (startDateTime) {
                    leagues = leagues.filter(x => 'showTime' in x && new Date(x.showTime) > startDateTime);
                }
                const endDateTime = filter.value?.endDateTime;
                if (endDateTime) {
                    leagues = leagues.filter(x => 'showTime' in x && new Date(x.showTime) < endDateTime);
                }

                return formatEvents(leagues);
            });

            function toggleIsExpandAll() {
                useRootStore().setIsExpandAll(!isExpandAll.value);
            }

            function refreshMatch(matchId: number, isCallApi: boolean) {
                if (isCallApi) {
                    if (marketPage.value === MarketPage.Outright) {
                        refetchOutrightTournaments();
                    } else {
                        refetchEvents();
                    }
                }
            }

            const showLeagueFilter = ref(false);

            function toggleFilter() {
                showLeagueFilter.value = !showLeagueFilter.value;
            }

            watch(() => marketPage.value, () => {
                window.scrollTo(0, 0);
            });
            watch(() => sportType.value, () => {
                window.scrollTo(0, 0);
                useRootStore().clearFilter();
            });
            watch(() => isMixParlayMode.value, () => {
                window.scrollTo(0, 0);
            });

            watch(() => isInAnyZone.value, () => {
                window.scrollTo(0, 0);
                useRootStore().clearFilter();
            }, { immediate: true });

            useRootStore().setIsExpandAll(true);
            useRootStore().clearFilter();

            const throttleRefreshMatch = throttle(refreshMatch, 5000);
            window.eventEmitter.on('refreshMatch', throttleRefreshMatch);
            window.eventEmitter.once('hook:beforeDestroy', () => {
                window.eventEmitter.off('refreshMatch', throttleRefreshMatch);
            });

            const router = useRouter();
            const route = useRoute();

            onBeforeRouteUpdate((to, from, next) => {
                const _route = router.options.routes?.find(x => x.name === route.name);
                if (_route?.beforeEnter) {
                    if (Array.isArray(_route.beforeEnter)) {
                        _route.beforeEnter.forEach(guard => guard.call(undefined, to, from, next));
                    } else {
                        _route.beforeEnter.call(undefined, to, from, next);
                    }
                } else {
                    next();
                }
            });

            const { isFromApp } = useApp();

            return {
                BannerPosition,
                i18n,
                isEuroSite,
                sportSwitchStyle,
                marketSwitchStyle,
                collapseStyle,
                isInEFootballZone,
                isInMultipleSportsZone,
                isInSimulationSportsZone,
                isInAnyZone,
                shouldShowNoMatchesMarketSwitchButton,
                leagueIds,
                leagueNames,
                isShowFlagsBanner,
                sportSwitchComponent,
                isShowMediumBetBuilderBetSlip,
                customerSettings,
                outrightTournaments,
                isMixParlayMode,
                marketPageEventCount,
                isWsConnected,
                filter,
                isExpandAll,
                isShowLiveInToday,
                thisPage,
                sportType,
                marketPage,
                isAsi,
                events,
                switchTo,
                showLeagueFilter,
                toggleFilter,
                toggleIsExpandAll,
                filteredLeagueEvents,
                leagueEvents,
                leagueComponent,
                haveEvent,
                eventsDisplay,
                loaded,
                isFromApp,
                routePage,
                isNewWidgetSwitchEnabled,
            };
        },
    });
</script>
